/**
 * Enterprise-grade React Error Boundary with Sentry integration
 *
 * This ErrorBoundary uses Sentry as the primary error tracking solution.
 * Benefits of using Sentry over custom logging:
 * - Automatic error grouping and deduplication
 * - Performance monitoring and release tracking
 * - Real-time alerts and notifications
 * - Advanced filtering and search capabilities
 * - User feedback collection
 * - Breadcrumb tracking for debugging context
 * - Integration with development workflows
 *
 * Role-based error display:
 * - Super Admin: Full technical details + stack traces
 * - Admin: Enhanced error info with some technical details
 * - Regular Users: User-friendly error messages
 */

import PropTypes from 'prop-types';
import React from 'react';
import { Alert, Button, Collapse, Typography, Space, Divider } from 'antd';
import { BugOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import encryptStorage from '../../util/encryption';

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      userFeedback: ''
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details to console for development
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);

    // Generate unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Enhanced error object with additional context
    const enhancedError = {
      ...error,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.getSessionId(),
      buildVersion: process.env.REACT_APP_VERSION || 'unknown',
      environment: process.env.NODE_ENV || 'unknown'
    };

    // Report to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', true);
      scope.setTag('errorId', errorId);
      scope.setLevel('error');
      scope.setTag('buildType', process.env.NODE_ENV);
      scope.setTag('userRole', this.getUserRole());
      scope.setTag('platform', 'web');
      scope.setTag('component', 'ErrorBoundary');

      // Add breadcrumb for this error
      Sentry.addBreadcrumb({
        message: 'Error caught by ErrorBoundary',
        category: 'error',
        level: 'error',
        data: {
          errorName: error.name,
          componentStack: errorInfo.componentStack.split('\n')[1] // First component in stack
        }
      });

      // Handle minified React errors specially
      if (error.message && error.message.includes('Minified React error')) {
        scope.setTag('errorType', 'minified_react_error');
        const errorUrl = error.message.match(/https:\/\/reactjs\.org\/docs\/error-decoder\.html[^"'\s]*/);
        if (errorUrl) {
          scope.setExtra('errorDecoderUrl', errorUrl[0]);
        }
        // Add specific breadcrumb for minified errors
        Sentry.addBreadcrumb({
          message: 'Minified React error detected',
          category: 'react',
          level: 'warning',
          data: { decoderUrl: errorUrl ? errorUrl[0] : 'none' }
        });
      }

      // Set user context for Sentry
      scope.setUser({
        id: this.getUserId(),
        role: this.getUserRole(),
        session_id: this.getSessionId()
      });

      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
        originalMessage: error.message,
        errorName: error.name,
        stackFrames: error.stack ? error.stack.split('\n').length : 0
      });

      scope.setContext('applicationContext', {
        buildVersion: enhancedError.buildVersion,
        environment: enhancedError.environment,
        nodeEnv: process.env.NODE_ENV,
        timestamp: enhancedError.timestamp
      });

      scope.setContext('browserContext', {
        userAgent: enhancedError.userAgent,
        url: enhancedError.url,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        referrer: document.referrer,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      });

      const sentryEventId = Sentry.captureException(error);

      this.setState({
        hasError: true,
        error: enhancedError,
        errorInfo,
        errorId,
        sentryEventId
      });
    });

    // Optional: Add user feedback capability to Sentry
    this.enableSentryUserFeedback(errorId);
  }

  getUserId = () => {
    try {
      // Use the encrypted storage to get user data (matches the Auth system)
      const user = encryptStorage.getItem('user');
      if (typeof user === 'string') {
        const parsedUser = JSON.parse(user);
        return parsedUser.id || parsedUser._id || 'anonymous';
      } else if (user && typeof user === 'object') {
        return user.id || user._id || 'anonymous';
      }
      return 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  getUserRole = () => {
    try {
      // Use the encrypted storage to get user data (matches the Auth system)
      const user = encryptStorage.getItem('user');
      if (typeof user === 'string') {
        const parsedUser = JSON.parse(user);
        return parsedUser.role?.name || 'user';
      } else if (user && typeof user === 'object') {
        return user.role?.name || 'user';
      }
      return 'user';
    } catch {
      return 'user';
    }
  };

  isSuperAdmin = () => {
    const roleName = this.getUserRole();
    // Based on the backend code, the exact role names are:
    // "Super Admin", "Admin", "Merchant"
    return roleName === 'Super Admin';
  };

  isAdminOrAbove = () => {
    const roleName = this.getUserRole();
    return roleName === 'Super Admin' || roleName === 'Admin';
  };

  getSessionId = () => {
    try {
      return sessionStorage.getItem('sessionId') || 'unknown';
    } catch {
      return 'unknown';
    }
  };

  enableSentryUserFeedback = (errorId) => {
    // Enhanced Sentry integration for user feedback and additional context
    try {
      if (this.state.sentryEventId) {
        // Add breadcrumb for error boundary activation
        Sentry.addBreadcrumb({
          message: 'ErrorBoundary UI displayed to user',
          category: 'ui',
          level: 'info',
          data: {
            errorId,
            sentryEventId: this.state.sentryEventId,
            userRole: this.getUserRole()
          }
        });

        // In production, you can enable Sentry's user feedback dialog
        if (process.env.NODE_ENV === 'production') {
          // Uncomment to enable Sentry's built-in user feedback dialog
          // Sentry.showReportDialog({ eventId: this.state.sentryEventId });
          console.info(`Error reported to Sentry with Event ID: ${this.state.sentryEventId}`);
        } else {
          console.info(`Error reported to Sentry (dev) with Event ID: ${this.state.sentryEventId}`);
        }
      }
    } catch (feedbackError) {
      console.warn('Failed to enable Sentry user feedback:', feedbackError);
      // Report the feedback error to Sentry as well
      Sentry.captureException(feedbackError, {
        tags: { component: 'ErrorBoundary', action: 'userFeedback' }
      });
    }
  };

  // Method to send additional user actions to Sentry
  reportUserAction = (action, data = {}) => {
    Sentry.addBreadcrumb({
      message: `User action: ${action}`,
      category: 'user',
      level: 'info',
      data: {
        ...data,
        errorId: this.state.errorId,
        timestamp: new Date().toISOString()
      }
    });
  };

  handleReload = () => {
    this.reportUserAction('reload_page', {
      url: window.location.href,
      userRole: this.getUserRole()
    });
    window.location.reload();
  };

  handleGoHome = () => {
    this.reportUserAction('navigate_home', {
      fromUrl: window.location.href,
      userRole: this.getUserRole()
    });
    window.location.href = '/app/dashboard';
  };

  copyErrorDetails = () => {
    const { error, errorInfo, errorId } = this.state;

    // Handle minified React errors
    let errorMessage = error?.message || 'Unknown error';
    let additionalInfo = '';

    if (errorMessage.includes('Minified React error')) {
      const errorUrl = errorMessage.match(/https:\/\/reactjs\.org\/docs\/error-decoder\.html[^"'\s]*/);
      if (errorUrl) {
        additionalInfo = `\nReact Error Decoder URL: ${errorUrl[0]}\nNote: This is a minified React error. Use the URL above for detailed information.`;
      }
    }

    const errorDetails = `
Error ID: ${errorId}
Timestamp: ${error?.timestamp}
Message: ${errorMessage}${additionalInfo}
URL: ${error?.url}
User Agent: ${error?.userAgent}
User Role: ${this.getUserRole()}
Build Version: ${error?.buildVersion}
Environment: ${error?.environment}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}

Additional Context:
- User ID: ${error?.userId}
- Session ID: ${error?.sessionId}
- Viewport: ${window.innerWidth}x${window.innerHeight}
- Referrer: ${document.referrer}
    `;

    navigator.clipboard.writeText(errorDetails).then(() => {
      this.reportUserAction('copy_error_details', {
        errorId,
        method: 'clipboard_api',
        userRole: this.getUserRole()
      });
      alert('Error details copied to clipboard');
    }).catch(() => {
      console.error('Failed to copy error details');
      // Fallback: create a text area and select it
      const textArea = document.createElement('textarea');
      textArea.value = errorDetails;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.reportUserAction('copy_error_details', {
        errorId,
        method: 'fallback_execCommand',
        userRole: this.getUserRole()
      });
      alert('Error details copied to clipboard (fallback method)');
    });
  };

  reportFeedback = () => {
    const { errorId, sentryEventId } = this.state;

    this.reportUserAction('open_feedback_dialog', {
      errorId,
      sentryEventId,
      userRole: this.getUserRole()
    });

    if (sentryEventId) {
      Sentry.showReportDialog({
        eventId: sentryEventId,
        title: 'Report Error',
        subtitle: `Error ID: ${errorId}`,
        subtitle2: 'Please describe what you were doing when this error occurred.'
      });
    } else {
      alert('Error reporting is not available at this time. Please contact support with the error details.');
    }
  };

  render() {
    const { children, fallback } = this.props;
    const { hasError, error, errorInfo, errorId } = this.state;
    const isSuperAdmin = this.isSuperAdmin();
    const isAdminOrAbove = this.isAdminOrAbove();

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Simple error view for regular users (non-admin)
      if (!isAdminOrAbove) {
        return (
          <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
            <Alert
              message="Something went wrong"
              description={
                <div>
                  <p>
                    We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
                  </p>
                  <p>
                    <Text strong>Error ID:</Text> <Text code>{errorId}</Text>
                  </p>
                </div>
              }
              type="error"
              showIcon
              icon={<BugOutlined />}
              action={
                <Space direction="vertical" size="small">
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={this.handleReload}
                    size="small"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    size="small"
                  >
                    Go to Dashboard
                  </Button>
                </Space>
              }
            />

            <Divider />

            <Alert
              message="Need Help?"
              description={
                <div>
                  <p>If this problem continues, please contact support:</p>
                  <ul>
                    <li>Email: <a href="mailto:support@kitegateway.com">support@kitegateway.com</a></li>
                    <li>Include Error ID: <Text code>{errorId}</Text></li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
            />
          </div>
        );
      }

      // Enhanced error view for admins (Admin and Super Admin)
      return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <Alert
            message={`Application Error (${isSuperAdmin ? 'Super Admin' : 'Admin'} View)`}
            description={
              <div>
                <p>
                  <Text strong>{isSuperAdmin ? '🔧 Super Admin' : '👤 Admin'} Error Details</Text>
                </p>
                <p>
                  Something went wrong. The error has been automatically reported to our team.
                </p>
                <p>
                  <Text strong>Error ID:</Text> <Text code>{errorId}</Text>
                </p>
                <p>
                  <Text strong>Time:</Text> {error?.timestamp && new Date(error.timestamp).toLocaleString()}
                </p>
                <p>
                  <Text strong>User Role:</Text> <Text code>{this.getUserRole()}</Text>
                </p>
              </div>
            }
            type="error"
            showIcon
            icon={<BugOutlined />}
            action={
              <Space direction="vertical" size="small">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                  size="small"
                >
                  Reload Page
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  size="small"
                >
                  Go to Dashboard
                </Button>
              </Space>
            }
          />

          <Divider />

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={this.copyErrorDetails}
                size="small"
              >
                Copy Error Details
              </Button>
              <Button
                onClick={this.reportFeedback}
                size="small"
              >
                Report Feedback
              </Button>
            </Space>

            <Collapse defaultActiveKey={process.env.NODE_ENV === 'development' ? ['1'] : []}>
              <Panel header={`🔍 Technical Error Details (${this.getUserRole()} Access)`} key="1">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Error Message:</Text>
                    <Paragraph code copyable>
                      {error?.message}
                    </Paragraph>
                    {error?.message?.includes('Minified React error') && (
                      <Alert
                        message="Minified React Error Detected"
                        description={
                          <div>
                            <p>This is a production build error. For detailed information:</p>
                            {error.message.match(/https:\/\/reactjs\.org\/docs\/error-decoder\.html[^"'\s]*/) && (
                              <p>
                                <a
                                  href={error.message.match(/https:\/\/reactjs\.org\/docs\/error-decoder\.html[^"'\s]*/)[0]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View React Error Details
                                </a>
                              </p>
                            )}
                            {error.message.includes('#185') && (
                              <p><Text strong>Error #185:</Text> This typically indicates a state update on an unmounted component. Check for async operations or subscriptions that aren't properly cleaned up.</p>
                            )}
                          </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </div>

                  {isSuperAdmin && (
                    <div>
                      <Text strong>Stack Trace:</Text>
                      <Paragraph code copyable style={{ whiteSpace: 'pre-wrap' }}>
                        {error?.stack}
                      </Paragraph>
                    </div>
                  )}

                  {isSuperAdmin && (
                    <div>
                      <Text strong>Component Stack:</Text>
                      <Paragraph code copyable style={{ whiteSpace: 'pre-wrap' }}>
                        {errorInfo?.componentStack}
                      </Paragraph>
                    </div>
                  )}

                  <div>
                    <Text strong>Additional Context:</Text>
                    <Paragraph code copyable>
                      {JSON.stringify({
                        url: error?.url,
                        userAgent: isSuperAdmin ? error?.userAgent : '[Hidden for Admin]',
                        buildVersion: error?.buildVersion,
                        userId: error?.userId,
                        sessionId: isSuperAdmin ? error?.sessionId : '[Hidden for Admin]',
                        userRole: this.getUserRole(),
                        timestamp: error?.timestamp
                      }, null, 2)}
                    </Paragraph>
                  </div>

                  {isSuperAdmin && process.env.NODE_ENV === 'development' && (
                    <div>
                      <Text strong>Development Environment Info:</Text>
                      <Paragraph code copyable>
                        {JSON.stringify({
                          nodeEnv: process.env.NODE_ENV,
                          reactVersion: React.version,
                          location: window.location.href,
                          viewport: `${window.innerWidth}x${window.innerHeight}`
                        }, null, 2)}
                      </Paragraph>
                    </div>
                  )}
                </Space>
              </Panel>
            </Collapse>

            <Alert
              message={`🛠 ${isSuperAdmin ? 'Super Admin' : 'Admin'} Support`}
              description={
                <div>
                  <p>As a{isSuperAdmin ? ' Super' : 'n'} Admin, you have access to {isSuperAdmin ? 'detailed' : 'enhanced'} error information:</p>
                  <ul>
                    <li>Technical Support: <a href={`mailto:${isSuperAdmin ? 'dev-support' : 'admin-support'}@kitegateway.com`}>
                      {isSuperAdmin ? 'dev-support' : 'admin-support'}@kitegateway.com</a></li>
                    <li>Include Error ID: <Text code>{errorId}</Text></li>
                    {isSuperAdmin && <li>Emergency Hotline: +1-800-KITE-DEV</li>}
                    {isSuperAdmin && <li>Slack: #engineering-alerts</li>}
                  </ul>
                  <p><Text type="secondary">
                    {isSuperAdmin
                      ? 'Regular users and Admins see simplified error messages without sensitive technical details.'
                      : 'Regular users see simplified error messages. Super Admins have access to full technical details.'}
                  </Text></p>
                </div>
              }
              type="info"
              showIcon
            />
          </Space>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
};

ErrorBoundary.defaultProps = {
  fallback: null,
};

export default ErrorBoundary;
